import { mapApiLoggerTagsToString, performanceNow, getInitialStartTime, } from 'js/file_viewer/core/riviera/api_logger';
import { RivieraStatus, RivieraError, TranscriptRivieraError, } from 'js/file_viewer/core/riviera/types';
import { docImageUrl } from 'js/file_viewer/core/utils/urls';
export const ImageLogger = (imageType, apiLogger) => {
    return {
        ...apiLogger,
        tags: {
            image_type: imageType,
            ...apiLogger.tags,
        },
    };
};
const logApiDuration = async (apiLogger, startTime, endTime) => {
    if (apiLogger.logDuration) {
        const log = await apiLogger.logDuration();
        if (log) {
            const stringifiedTags = await mapApiLoggerTagsToString(apiLogger.tags);
            const initialStartTime = await getInitialStartTime(apiLogger.tags);
            log({
                eventName: 'prefetch.time_to_start_prefetch',
                durationInMs: startTime - initialStartTime,
                session: undefined,
                tags: stringifiedTags,
                shouldLogToHive: true,
            });
            log({
                eventName: 'prefetch.prefetch_duration',
                durationInMs: endTime - startTime,
                session: undefined,
                tags: stringifiedTags,
                shouldLogToHive: true,
            });
            log({
                eventName: 'prefetch.time_to_complete_prefetch',
                durationInMs: endTime - initialStartTime,
                session: undefined,
                tags: stringifiedTags,
                shouldLogToHive: true,
            });
        }
    }
};
const imageCache = new Set();
const loadImage = async (url, srcSetUrl, sizes, logger) => new Promise((resolve, reject) => {
    const startTime = performanceNow();
    const img = new Image();
    img.onload = () => {
        const key = `${url}:${srcSetUrl}:${sizes}`;
        if (logger && !imageCache.has(key)) {
            imageCache.add(key);
            logger.tags = { status: 'success', ...logger.tags };
            logApiDuration(logger, startTime, performanceNow());
        }
        resolve(url);
    };
    img.onerror = () => {
        if (logger) {
            logger.tags = { status: 'error', ...logger.tags };
            logApiDuration(logger, startTime, performanceNow());
        }
        reject(new RivieraError('Failed to load image', RivieraStatus.Unknown));
    };
    if (srcSetUrl && sizes) {
        img.srcset = srcSetUrl;
        img.sizes = sizes;
    }
    img.src = url;
});
function handlePreviewUrlError(status, metadataHeader, pdfPasswordNeeded) {
    if (status === 401 && pdfPasswordNeeded) {
        return new RivieraError('Password required', RivieraStatus.PasswordProtected);
    }
    return new RivieraError(`Failed to load with status ${status}`, metadataHeader.rivieraStatusCode);
}
/**
 * Parses and returns any results in the 'x-dropbox-metadata' header. Note: If any error occurs
 * (e.g. the header is missing or malformed), this function is still guaranteed to return an
 * object.
 */
const getParsedDropboxMetadataHeader = (dropboxMetadataHeader) => {
    let dropboxMetadata = {};
    try {
        if (dropboxMetadataHeader) {
            dropboxMetadata = JSON.parse(dropboxMetadataHeader);
        }
    }
    catch { }
    return dropboxMetadata;
};
function textUrl(url, metadata, startPage = 0, endPage = 50) {
    const newUrl = new URL(url);
    if (metadata) {
        newUrl.searchParams.set('metadata', '1');
    }
    if (startPage !== undefined) {
        newUrl.searchParams.set('text', '1');
        newUrl.searchParams.set('page_start', startPage.toString());
        newUrl.searchParams.set('page_end', endPage.toString());
    }
    return newUrl.toString();
}
const extractData = async (resp) => resp === null || resp === void 0 ? void 0 : resp.json();
const fetchData = async (...args) => _fetch(extractData, args);
const fetchTextData = async (...args) => _fetch(async (res) => res === null || res === void 0 ? void 0 : res.arrayBuffer(), args);
const extractOpaque = async () => ({});
/** Used for prefetching where the response is not read */
const fetchOpaque = (...args) => _fetch(extractOpaque, args);
const _fetch = async (extractor, args) => {
    var _a, _b, _c, _d;
    let resp;
    try {
        resp = await fetch(...args);
    }
    catch {
        resp = undefined;
    }
    const ok = (_a = resp === null || resp === void 0 ? void 0 : resp.ok) !== null && _a !== void 0 ? _a : false;
    const status = (_b = resp === null || resp === void 0 ? void 0 : resp.status) !== null && _b !== void 0 ? _b : 400;
    const dropboxMetadata = getParsedDropboxMetadataHeader((_c = resp === null || resp === void 0 ? void 0 : resp.headers.get('x-dropbox-metadata')) !== null && _c !== void 0 ? _c : undefined);
    const passwordNeededHeader = (_d = resp === null || resp === void 0 ? void 0 : resp.headers.get('x-dropbox-pdf-password-needed')) !== null && _d !== void 0 ? _d : undefined;
    let data;
    try {
        data = await extractor(resp);
    }
    catch {
        data = undefined;
    }
    if (ok && data) {
        return { data: data };
    }
    const passwordNeeded = passwordNeededHeader === '1';
    return {
        error: handlePreviewUrlError(status, dropboxMetadata, passwordNeeded),
    };
};
export const rivieraClient = {
    refreshDocBlob(refreshUrl, password) {
        return fetchData(refreshUrl, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            },
            body: new URLSearchParams({ password: password || '' }),
            credentials: 'include',
            method: 'POST',
        });
    },
    fetchDocMetadata(textUrlTmpl, metadata = false, startPage = 0, endPage = 50) {
        return fetchData(textUrl(textUrlTmpl, metadata, startPage, endPage), {
            headers: {
                'content-type': 'application/json',
            },
            credentials: 'include',
        });
    },
    fetchMediaMetadata(url) {
        return fetchData(url, {
            credentials: 'include',
        });
    },
    fetchMasterPlaylist(url) {
        return fetchOpaque(url, {
            credentials: 'include',
        });
    },
    fetchWaveformData(url) {
        return fetchData(url, {
            credentials: 'include',
        });
    },
    async fetchTranscriptData(url) {
        var _a, _b;
        const response = await fetchData(url, {
            credentials: 'include',
            // Transcripts are invalidated when the file changes, such as after video trimming. We do not want to cache this
            // response because whether or not we show a transcript may change frequently.
            cache: 'no-cache',
        });
        let error = response.error;
        if (!error && ((_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.segments) === null || _b === void 0 ? void 0 : _b.length) === 0) {
            error = new TranscriptRivieraError('no_speech', RivieraStatus.Unknown);
        }
        if (error) {
            return { error };
        }
        return response;
    },
    fetchLinkfileData(url, authenticated) {
        return fetchData(url, {
            credentials: authenticated ? 'include' : 'omit',
            headers: {
                'content-type': 'application/json',
            },
            method: 'POST',
        });
    },
    fetchArchiveTopLevelData(contentUrl) {
        return fetchData(contentUrl, {
            headers: {
                'content-type': 'application/json',
            },
            credentials: 'include',
            method: 'POST',
        });
    },
    fetchDocImage: ({ imageUrlTmpl, width, scalePercent, page, }) => {
        const url = docImageUrl({ page, imageUrlTmpl }, scalePercent ? { scalePercent } : { width });
        return loadImage(url)
            .then(() => ({ data: url }))
            .catch((error) => {
            return { error };
        });
    },
    fetchTextContent: (extractedTextUrl) => {
        return fetchTextData(extractedTextUrl, {
            credentials: 'include',
        });
    },
    fetchImage: (url, srcSetUrl, sizes, logger) => loadImage(url, srcSetUrl, sizes, logger)
        .then(() => ({ data: url }))
        .catch((error) => {
        return { error };
    }),
};
//# sourceMappingURL=api.preenvesnextjs.map
